import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// Local YYYY-MM-DD (avoid UTC drift from .toISOString()).
function toIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

// Six fixed presets keyed by id. ISO weeks (Mon→Sun).
function buildPresets(now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);

  const dow = (today.getDay() + 6) % 7;             // 0=Mon..6=Sun
  const thisWeekStart = new Date(today); thisWeekStart.setDate(thisWeekStart.getDate() - dow);
  const lastWeekStart = new Date(thisWeekStart); lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  const lastWeekEnd   = new Date(thisWeekStart); lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);

  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd   = new Date(today.getFullYear(), today.getMonth(), 0); // day 0 = last of prev

  return {
    today:     { label: 'Today',      from: toIsoDate(today),         to: toIsoDate(today) },
    yesterday: { label: 'Yesterday',  from: toIsoDate(yesterday),     to: toIsoDate(yesterday) },
    thisWeek:  { label: 'This Week',  from: toIsoDate(thisWeekStart), to: toIsoDate(today) },
    lastWeek:  { label: 'Last Week',  from: toIsoDate(lastWeekStart), to: toIsoDate(lastWeekEnd) },
    thisMonth: { label: 'This Month', from: toIsoDate(thisMonthStart), to: toIsoDate(today) },
    lastMonth: { label: 'Last Month', from: toIsoDate(lastMonthStart), to: toIsoDate(lastMonthEnd) }
  };
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtInt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('th-TH');
}

// Render YYYY-MM as "MMM YYYY" in English to keep axis labels narrow.
function fmtMonth(ym) {
  if (!ym || ym.length < 7) return ym || '';
  const [y, m] = ym.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[Number(m) - 1] || m} ${y}`;
}

const STATUS_COLORS = {
  paid:      '#2e7d32',
  accepted:  '#43a047',
  sent:      '#1976d2',
  created:   '#616161',
  rejected:  '#c62828',
  error:     '#c62828',
  cancelled: '#757575',
  unknown:   '#9e9e9e'
};

function KpiCard({ label, value, sub, accent = '#1976d2' }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 8, padding: '14px 16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderTop: `3px solid ${accent}`,
      flex: '1 1 200px', minWidth: 180
    }}>
      <div style={{ color: '#666', fontSize: 12, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#222', marginTop: 6 }}>{value}</div>
      {sub && <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function APFSDashboard() {
  const presets = useMemo(() => buildPresets(new Date()), []);
  const [activePreset, setActivePreset] = useState('thisMonth');
  const [from, setFrom] = useState(presets.thisMonth.from);
  const [to, setTo] = useState(presets.thisMonth.to);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchDashboard = useCallback(async (f, t) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (f) params.append('date_from', f);
      if (t) params.append('date_to', t);
      const resp = await axios.get(`${API_BASE_URL}/api/apfs/dashboard?${params}`);
      if (resp.data.success) {
        setData(resp.data);
      } else {
        setError(resp.data.error || 'Failed to load dashboard');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(from, to); }, [from, to, fetchDashboard]);

  const applyPreset = (key) => {
    const p = presets[key];
    if (!p) return;
    setActivePreset(key);
    setFrom(p.from);
    setTo(p.to);
  };

  const onCustomDate = (which, value) => {
    setActivePreset('custom');
    if (which === 'from') setFrom(value); else setTo(value);
  };

  const summary = data?.summary || { totalGrossPremium: 0, totalAfterDiscount: 0, propIdCount: 0, paidAcceptedCount: 0, statusBreakdown: {} };
  const byMonth = data?.byMonth || [];

  const statusEntries = Object.entries(summary.statusBreakdown || {})
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className="po-submitted">
      <h2>APFS Dashboard</h2>

      {/* Preset + custom range controls */}
      <div style={{
        background: '#fff', borderRadius: 8, padding: 14, marginBottom: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {Object.entries(presets).map(([key, p]) => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              style={{
                padding: '6px 12px', borderRadius: 16, fontSize: 13, fontWeight: 600,
                border: '1px solid ' + (activePreset === key ? '#1976d2' : '#cfd8dc'),
                background: activePreset === key ? '#1976d2' : '#fff',
                color: activePreset === key ? '#fff' : '#37474f',
                cursor: 'pointer'
              }}
            >
              {p.label}
            </button>
          ))}

          <div style={{ width: 1, height: 26, background: '#e0e0e0', margin: '0 6px' }} />

          <label style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center', gap: 6 }}>
            From
            <input type="date" value={from} max={to || undefined}
                   onChange={(e) => onCustomDate('from', e.target.value)}
                   style={{ padding: '4px 6px' }} />
          </label>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center', gap: 6 }}>
            To
            <input type="date" value={to} min={from || undefined}
                   onChange={(e) => onCustomDate('to', e.target.value)}
                   style={{ padding: '4px 6px' }} />
          </label>

          <button
            onClick={() => fetchDashboard(from, to)}
            style={{
              marginLeft: 'auto', padding: '6px 14px', borderRadius: 4,
              background: '#1976d2', color: '#fff', border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: 13
            }}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {(from || to) && (
          <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
            Showing <b>{from || '…'}</b> → <b>{to || '…'}</b>
            {activePreset !== 'custom' && presets[activePreset] && ` · ${presets[activePreset].label}`}
          </div>
        )}
      </div>

      {error && (
        <div style={{ color: '#c62828', background: '#ffebee', padding: 12, borderRadius: 4, marginBottom: 16 }}>
          ⚠️ {error}
        </div>
      )}

      {/* KPI cards */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <KpiCard
          label="Gross Premium"
          value={`฿ ${fmtMoney(summary.totalGrossPremium)}`}
          sub={`After discount: ฿ ${fmtMoney(summary.totalAfterDiscount)}`}
          accent="#1976d2"
        />
        <KpiCard
          label="Prop IDs"
          value={fmtInt(summary.propIdCount)}
          sub="Total submissions in range"
          accent="#6a1b9a"
        />
        <KpiCard
          label="Paid / Accepted"
          value={fmtInt(summary.paidAcceptedCount)}
          sub={summary.propIdCount
            ? `${((summary.paidAcceptedCount / summary.propIdCount) * 100).toFixed(1)}% conversion`
            : '—'}
          accent="#2e7d32"
        />
        <KpiCard
          label="Avg Premium / Prop"
          value={summary.propIdCount
            ? `฿ ${fmtMoney(summary.totalGrossPremium / summary.propIdCount)}`
            : '—'}
          sub="Gross premium ÷ count"
          accent="#ef6c00"
        />
        <KpiCard
          label="EOD Premium (received)"
          value={`฿ ${fmtMoney(summary.eodPremium || 0)}`}
          sub={`${fmtInt(summary.eodCount || 0)} EOD txns · Transfer 60%: ฿ ${fmtMoney(summary.eodTransfer || 0)}`}
          accent="#0f9d58"
        />
      </div>

      {/* Status breakdown chips */}
      <div style={{
        background: '#fff', borderRadius: 8, padding: 14, marginBottom: 20,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>Status breakdown</div>
        {statusEntries.length === 0 ? (
          <div style={{ color: '#999', fontSize: 13 }}>No submissions in this period.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {statusEntries.map(([status, count]) => {
              const color = STATUS_COLORS[status] || '#616161';
              return (
                <div key={status} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: `${color}14`, color, padding: '4px 12px',
                  borderRadius: 14, fontSize: 13, fontWeight: 600
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                  {status}
                  <span style={{ color: '#333', fontWeight: 700 }}>· {fmtInt(count)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Combo chart: bars = Gross Premium, line = Prop ID count */}
      <div style={{
        background: '#fff', borderRadius: 8, padding: 14,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>
          Break by Month — Gross Premium (bar) + Prop ID count (line)
        </div>
        {byMonth.length === 0 ? (
          <div style={{ color: '#999', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>
            {loading ? 'Loading…' : 'No data to chart for this period.'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={byMonth} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
              <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
              <XAxis dataKey="month" tickFormatter={fmtMonth} />
              <YAxis yAxisId="left"
                     tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                     label={{ value: 'Gross Premium (THB)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#666' } }} />
              <YAxis yAxisId="right" orientation="right" allowDecimals={false}
                     label={{ value: 'Prop IDs', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#666' } }} />
              <Tooltip
                labelFormatter={fmtMonth}
                formatter={(value, name) => {
                  if (name === 'Prop IDs') return [fmtInt(value), name];
                  return [`฿ ${fmtMoney(value)}`, name];
                }}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="grossPremium" name="Gross Premium" fill="#1976d2" barSize={36} />
              <Line yAxisId="right" type="monotone" dataKey="count" name="Prop IDs"
                    stroke="#ef6c00" strokeWidth={2.5} dot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default APFSDashboard;
